import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { ExpeditionTypeEnum, LovWithoutIdDTO } from '@api/ringil-api.v2.tsx';
import SvgFtlLtl from '@icons/FtlLtl.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { ApiContext } from '@api/api.tsx';
import { stampPrimeDropdownOverlayTestIds } from '@utils/testUtils';

interface ComponentProps {
  secured?: SecuredConfig;
  value?: ExpeditionTypeEnum;
  readOnly?: boolean;
  required?: boolean;
  errorMessage?: string;
  // null = the user cleared the type (shipment type is optional, RD-2961).
  onChange: (expeditionType: ExpeditionTypeEnum | null) => void;
}

const ExpeditionTypeTag: React.FC<ComponentProps> = ({ secured, value, readOnly, required, errorMessage, onChange }) => {
  const { tr } = useTranslationLgs();
  const [internalValue, setInternalValue] = useState<ExpeditionTypeEnum | null>(value ?? null);

  useEffect(() => {
    setInternalValue(value ?? null);
  }, [value]);

  const listTestId = 'expeditionInfo-transportationTypeList';

  const applyOverlayTestIds = () => stampPrimeDropdownOverlayTestIds(listTestId);

  const { lovExpeditionTypes } = useContext(ApiContext);

  const [expeditionTypes, setExpeditionTypes] = useState<LovWithoutIdDTO[]>([]);

  useEffect(() => {
    lovExpeditionTypes(d => {
      setExpeditionTypes(d);
    });
  }, []);


  const renderText = (code: ExpeditionTypeEnum) => {
    if (code == null) return null;
    return tr(`ExpeditionTypeTag.${code}`, `ExpeditionTypeTag.${code}`);
  };

  const renderItemTemplateText = (item: LovWithoutIdDTO) => {
    return renderText(item.code as ExpeditionTypeEnum);
  };

  return (
    <>
      <ButtonTag secured={secured}
        label={tr('ExpeditionTypeTag.title', 'Typ přepravy')}
        required={required}
        errorMessage={errorMessage}
        readonly={readOnly}
        value={renderText(value)}
        onSave={onSuccess => {
          onChange(internalValue);
          onSuccess();
        }}
        icon={<SvgFtlLtl />}
        autoClose
        dataTestId="expeditionInfo-transportationType"
      >
        <div data-testid={listTestId}>
          <Dropdown
            className="w-full"
            options={expeditionTypes}
            value={internalValue}
            optionLabel="label"
            optionValue={'code'}
            showClear
            panelClassName="expeditionInfoDropdownPanel"
            onShow={() => {
              requestAnimationFrame(applyOverlayTestIds);
            }}
            onChange={e => setInternalValue(e.target.value ?? null)}
            itemTemplate={i => {
              const label = renderItemTemplateText(i);
              return <div>{label}</div>;
            }}
            valueTemplate={i => <div>{i != null ? renderItemTemplateText(i) : tr('global.expeditionTypeNotSpecified', 'Neuvedeno')}</div>}
          />
        </div>
      </ButtonTag>
    </>
  );
};

export default ExpeditionTypeTag;
